import { useState } from 'react';

import { Callout, EmailInput, Field, Panel, PasswordInput, TextInput } from './ui.tsx';
import { Icon } from './icons.tsx';
import { kitText } from './messages.ts';
import { readLogin, type LoginOutcome } from './session.ts';

/**
 * The sign-in screen.
 *
 * §3.7's second factor is a *step*, not an error: the form asks for a code
 * after the password is accepted, and says so. Treating it as a failure is how
 * people learn to retype their password when the code box appears.
 *
 * The session survives a reload, but the token itself never touches
 * `localStorage` or any script-readable storage — it rides an `HttpOnly`
 * cookie the browser attaches on its own, which JavaScript on the page
 * (including an injected script) cannot read even during the session it
 * belongs to. §3.10a still applies: each console's cookie is scoped to its
 * own origin only, never shared between them.
 */
export function SignIn({
  title,
  logoUrl,
  brandName,
  onSignedIn,
  onNeedsEnrolment,
  onMustChangePassword,
  onCreateAccount,
  post,
}: {
  title: string;
  /** A brand's own host: its logo and name lead the screen. */
  logoUrl?: string;
  brandName?: string;
  onSignedIn: (token: string, permissions: readonly string[], userId: string) => void;
  /**
   * §3.7 — the password was right and there is no device yet.
   *
   * The credentials go with it because enrolment and the password change both
   * still need them and there is no session to carry them: handing them onward
   * is what keeps the person from typing the same password three times.
   */
  onNeedsEnrolment?: (email: string, password: string) => void;
  onMustChangePassword?: (email: string, password: string) => void;
  /**
   * §50.1 — only the AffDice Admin passes this.
   *
   * Optional rather than always shown: an affiliate never signs itself up. They
   * arrive by invitation from an operator (§6.14a), so a "create an account"
   * link on their portal would offer a route that does not exist.
   */
  onCreateAccount?: () => void;
  post: (body: { email: string; password: string; code?: string }) => Promise<{
    status: number;
    body: unknown;
  }>;
}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [stage, setStage] = useState<'password' | 'code'>('password');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [prompt, setPrompt] = useState<string | null>(null);

  const submit = () => {
    setBusy(true);
    setProblem(null);
    post({ email, password, ...(stage === 'code' ? { code } : {}) })
      .then((r) => {
        const outcome: LoginOutcome = readLogin(r.status, r.body);
        if (outcome.kind === 'mfa') {
          // A step, not a failure. Said in its own words so nobody retypes a
          // password that was already accepted.
          setStage('code');
          setPrompt(outcome.message);
          return;
        }
        if (outcome.kind === 'enrol') {
          if (onNeedsEnrolment) { onNeedsEnrolment(email, password); return; }
          setProblem(outcome.message);
          return;
        }
        if (outcome.kind === 'signed-in') {
          // §3.7 — an administrator-set password is one two people know, so it
          // is changed before anything else rather than after a reminder.
          if (outcome.mustChangePassword && onMustChangePassword) {
            onMustChangePassword(email, password);
            return;
          }
          onSignedIn(outcome.token, outcome.permissions, outcome.userId);
          return;
        }
        setProblem(outcome.message);
      })
      .catch(() =>
        setProblem(kitText('signin.unreachable')),
      )
      .finally(() => setBusy(false));
  };

  return (
    <div className="main signin">
      {logoUrl || brandName ? (
        <div className="signin-brand">
          {logoUrl ? (
            <span className="signin-brand-logo"><img src={logoUrl} alt="" /></span>
          ) : null}
          {brandName ? <span className="signin-brand-name">{brandName}</span> : null}
        </div>
      ) : null}
      <div className="page-head"><h1>{title}</h1></div>

      <Panel title={stage === 'password' ? kitText('signin.panel') : kitText('signin.panel.code')}>
        <div className="form">
          {problem ? <Callout tone="error">{problem}</Callout> : null}
          {stage === 'code' && prompt ? (
            <Callout>{prompt}</Callout>
          ) : null}

          {stage === 'password' ? (
            <>
              <Field label={kitText('field.email')}>
                <EmailInput value={email} onChange={setEmail} placeholder="you@example.com" />
              </Field>
              <Field label={kitText('field.password')}>
                <PasswordInput
                  value={password}
                  onChange={setPassword}
                  autoComplete="current-password"
                  onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
                />
              </Field>
            </>
          ) : (
            <Field
              label={kitText('field.code')}
              hint={kitText('field.code.hint')}
            >
              <TextInput
                value={code}
                onChange={setCode}
                placeholder="000000"
                onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
              />
            </Field>
          )}

          <div className="form-actions">
            <button
              className="primary"
              disabled={busy || (stage === 'password' ? email === '' || password === '' : code === '')}
              onClick={submit}
            >
              {busy
                ? kitText('action.checking')
                : stage === 'password'
                  ? kitText('signin.panel')
                  : kitText('signin.verify')}
            </button>

            {stage === 'password' && onCreateAccount ? (
              <button className="link-button" onClick={onCreateAccount}>
                {kitText('signin.createAccount')}
              </button>
            ) : null}
          </div>
        </div>
      </Panel>
    </div>
  );
}

/**
 * Signing out.
 *
 * §3.7 — revoked at the server, not merely forgotten here. A token dropped from
 * memory while still live on the server is one that keeps working for whoever
 * copied it, and "I signed out" would be false.
 */
export function SignOut({ onDone, revoke }: { onDone: () => void; revoke: () => Promise<unknown> }) {
  const [busy, setBusy] = useState(false);
  return (
    <button
      className="theme-toggle"
      disabled={busy}
      onClick={() => {
        setBusy(true);
        // Cleared locally whatever the server said. A revoke that failed still
        // means this browser should stop holding the token.
        void revoke().catch(() => {}).finally(onDone);
      }}
    >
      <Icon name="sign-out" size={15} />
      {busy ? kitText('signout.busy') : kitText('signout.action')}
    </button>
  );
}
